/**
 * E03-F04-T07 — Stage-3 Generation surface. The recruiter's document-package
 * dashboard: one card per item in GENERATION_ORDER (Kickoff Sheet → Follow-up
 * Email → JD → InMails → Interview Questions) plus an Internal Notification block,
 * a "Generate All Docs" button with a staged spinner, generated/approved counters,
 * an AI-Instructions textarea (`generation_notes`, saved onblur) and a Kickoff
 * Data side panel. 1:1 clone of the legacy `renderStage4Content` / `_renderGenCard`.
 *
 * Status, action and counter decisions are PURE (docGenerationModel.ts); the AI
 * calls go through the typed mutations (docGenerationApi.ts). The component only
 * orchestrates: render cards from the artifact map, fire the right mutation per
 * card (special items → their aiAction; core docs → regenerate_document after the
 * first generate_documents batch), and surface loading/labels.
 */
import { useState, type JSX } from 'react';
import {
  GENERATION_LABELS,
  GENERATION_FIRST_ACTION,
  GENERATE_ALL_STEPS,
  GENERATE_DOCUMENTS_TYPES,
  packageGateState,
  type GenerationOrderItem,
} from '@eer/shared';
import { Dialog, DialogFooter } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import {
  GENERATION_CARDS,
  artifactStatusOf,
  badgeFor,
  cardActionsFor,
  countApproved,
  countGenerated,
  notificationSentLabel,
  type ArtifactMap,
  type GenerationCardType,
} from './docGenerationModel.js';
import {
  useGenerateDocuments,
  useGenerateFollowupEmail,
  useGenerateKickoffSheet,
  useRegenerateDocument,
  useApproveArtifact,
} from './docGenerationApi.js';
import type { RegeneratableDocType } from '@eer/shared';

export interface GenerationStageViewProps {
  readonly projectId: number;
  /** Current artifacts keyed by type (from the project detail). */
  readonly artifacts: ArtifactMap;
  /** Pre-filled AI instructions (projects.data.generation_notes). */
  readonly generationNotes?: string;
  /** Persist the generation_notes (saveGenerationNotes — onblur). */
  readonly onSaveGenerationNotes?: (notes: string) => void;
  /** Open the editor / preview for a doc type (wired by the host shell). */
  readonly onOpenEditor?: (type: string) => void;
  /** Open the Internal-Notification («Kickoff done mail») send dialog (E03-F07-T05). */
  readonly onSendNotification?: () => void;
  /**
   * E03-F07-T07 — when the «Kickoff done mail» was last sent
   * (`project.internal_notification_sent_at`); drives the card's Sent badge.
   */
  readonly notificationSentAt?: string | null;
  /** Download an approved doc as a `.doc` file (legacy downloadArtifact). */
  readonly onDownload?: (type: string) => void;
  /** Open the send-email dialog for an approved communication doc. */
  readonly onSend?: (type: string) => void;
  /** Open the Send-to-Sourcing dialog for the kickoff sheet. */
  readonly onSendToSourcing?: () => void;
  /**
   * Open the "Confirm & Move to Final" gate (host calls confirm_package + advances
   * the stage). Rendered only when supplied AND every core doc is approved.
   */
  readonly onConfirmPackage?: () => void;
  /** True while the confirm_package + stage advance is in flight. */
  readonly confirming?: boolean;
  /** Optional Kickoff Data panel content (sidebar). */
  readonly kickoffData?: JSX.Element;
}

/**
 * Per-card display metadata (legacy `_renderGenCard` icon + subtitle). Kept in the
 * web layer — purely presentational, mirrors the v1 Generation card look (a doc
 * icon + a one-line description above the action row).
 */
const GENERATION_ICONS: Record<
  GenerationOrderItem | 'internal_notification' | 'sourcing_sheet',
  string
> = {
  kickoff_sheet: '📋',
  client_followup_email: '✉️',
  jd_doc: '📄',
  inmails: '💬',
  interview_questions: '❓',
  internal_notification: '🔔',
  sourcing_sheet: '🎯',
};

/**
 * Doc types the `generate_documents` batch step is expected to produce, in
 * order. Used only to label progress during that step (there is no per-doc
 * backend SSE stream) — see {@link GenerationStageView} `generateAll`
 * (E03-F04-T10 / E03-F04-T09).
 *
 * Sourced from the shared `GENERATE_DOCUMENTS_TYPES` (the single source of truth
 * for what the batch actually produces) so the cycling denominator can never
 * drift from the real batch size again (ISSUE-2: it used to hardcode 3 and drop
 * `sourcing_sheet`, so the label read "of 3" while the batch produced 4).
 */
const GENERATE_ALL_BATCH_DOCS = GENERATE_DOCUMENTS_TYPES;

type GenerateAllAction = (typeof GENERATE_ALL_STEPS)[number]['action'];

/** Human names of the Generate-All steps, used in the partial-failure toast. */
const GENERATE_ALL_STEP_NAMES: Record<GenerateAllAction, string> = {
  generate_kickoff_sheet: 'Kickoff Sheet',
  generate_documents: 'Documents (JD, Interview Questions, InMails, Sourcing Sheet)',
  generate_followup_email: 'Follow-up Email',
};

/**
 * Human labels for the batch doc types. `sourcing_sheet` is NOT part of
 * `GENERATION_LABELS` (it is not a generation-order card), so it needs its own
 * label for the progress ticker.
 */
const BATCH_DOC_LABELS: Record<(typeof GENERATE_DOCUMENTS_TYPES)[number], string> = {
  jd_doc: 'Job Description',
  interview_questions: 'Interview Questions',
  inmails: 'InMails',
  sourcing_sheet: 'Sourcing Sheet',
};

const GENERATION_DESCRIPTIONS: Record<
  GenerationOrderItem | 'internal_notification' | 'sourcing_sheet',
  string
> = {
  kickoff_sheet: 'Full project briefing document',
  client_followup_email: 'Post kick-off email to client',
  jd_doc: 'LinkedIn job description',
  inmails: 'LinkedIn message templates',
  interview_questions: 'Candidate interview questions',
  internal_notification: 'Notify the internal team the package is ready',
  sourcing_sheet: 'Sourcing criteria for the recruiting team',
};

/** A status badge pill. */
function StatusBadge({ type, map }: { type: string; map: ArtifactMap }): JSX.Element {
  const status = artifactStatusOf(map, type);
  const { label, toneClass } = badgeFor(status);
  return (
    <span
      data-testid={`gen-badge-${type}`}
      data-status={status}
      className={`text-xs font-medium px-2 py-0.5 rounded-full ${toneClass}`}
    >
      {label}
    </span>
  );
}

export function GenerationStageView({
  projectId,
  artifacts,
  generationNotes = '',
  onSaveGenerationNotes,
  onOpenEditor,
  onSendNotification,
  notificationSentAt = null,
  onDownload,
  onSend,
  onSendToSourcing,
  onConfirmPackage,
  confirming = false,
  kickoffData,
}: GenerationStageViewProps): JSX.Element {
  const toast = useToast();
  const notifSentLabel = notificationSentLabel(notificationSentAt);
  const [notes, setNotes] = useState(generationNotes);
  const [generateAllStep, setGenerateAllStep] = useState<string | null>(null);
  /**
   * The doc type of the single card whose generate/regenerate is in flight. Only
   * that card locks (shows a spinner) — every other card stays clickable. Replaces
   * the old shared `busy` flag that disabled every card at once (E03-F08-T02).
   */
  const [activeType, setActiveType] = useState<string | null>(null);
  /**
   * Card-level Regenerate dialog state (E03-F05-T09 / ISSUE-4). `regenTarget`
   * is the doc type whose Instruction dialog is open (null → closed); the
   * mutation only fires on confirm, so the recruiter can add an instruction
   * before the regenerate call goes out.
   */
  const [regenTarget, setRegenTarget] = useState<GenerationCardType | null>(null);
  const [regenInstruction, setRegenInstruction] = useState('');

  const genDocs = useGenerateDocuments(projectId);
  const genKickoff = useGenerateKickoffSheet(projectId);
  const genFollowup = useGenerateFollowupEmail(projectId);
  const regen = useRegenerateDocument(projectId);
  const approve = useApproveArtifact(projectId);

  const generated = countGenerated(artifacts);
  const approved = countApproved(artifacts);
  /**
   * Confirm gate — evaluated by the SHARED `packageGateState` (E03-F05-T05), the
   * same predicate the endpoint counts with. It used to be spelled out here over
   * `GENERATION_CARDS` with a local `approved || sent` check, which disagreed with
   * the server in BOTH directions: the button lit up for a `sent` package doc the
   * server refused to count, and stayed dark while the two communication cards
   * (Kickoff Sheet / Follow-up Email) were unapproved even though they are not part
   * of the gate at all (legacy Confirm.md counts exactly the 4 PACKAGE_DOC_TYPES).
   * Never re-derive this locally — read the shared helper.
   */
  const gate = packageGateState((t) => artifactStatusOf(artifacts, t));
  const allApproved = gate.allApproved;

  /** First generation for one card → its dedicated action / generate_documents. */
  async function generateCard(type: GenerationCardType): Promise<void> {
    setActiveType(type);
    try {
      // sourcing_sheet is part of the SAME generate_documents batch as
      // jd_doc / inmails / interview_questions (GENERATE_DOCUMENTS_TYPES), so its
      // first generation fires the batch call too. Handle it before indexing
      // GENERATION_FIRST_ACTION (which only covers the GENERATION_ORDER items).
      if (type === 'sourcing_sheet') {
        await genDocs.mutateAsync(notes || undefined);
      } else {
        const action = GENERATION_FIRST_ACTION[type];
        if (action === 'generate_kickoff_sheet') {
          await genKickoff.mutateAsync(notes || undefined);
        } else if (action === 'generate_followup_email') {
          await genFollowup.mutateAsync(notes || undefined);
        } else {
          // jd_doc / inmails / interview_questions are produced by the batch.
          await genDocs.mutateAsync(notes || undefined);
        }
      }
      toast.success(`${GENERATION_LABELS[type]} generated`);
    } catch {
      toast.error(`Failed to generate ${GENERATION_LABELS[type]}`);
    } finally {
      setActiveType(null);
    }
  }

  /**
   * Card-level Regenerate → open the Instruction dialog FIRST (E03-F05-T09 /
   * ISSUE-4). The mutation does NOT fire here: it only fires on confirm
   * ({@link confirmRegenerate}), so the recruiter can add a regeneration
   * instruction before the call goes out — instead of the old behaviour that
   * fired immediately with an empty instruction. Mirrors the DocumentEditor
   * "Regenerate document" dialog.
   */
  function regenerateCard(type: GenerationCardType): void {
    setRegenTarget(type);
    setRegenInstruction('');
  }

  /**
   * Confirm the regenerate dialog → fire the right mutation with the single
   * "Regeneration instructions" field (E03-F05-T09 / ISSUE-1). The client asked
   * for ONE field, so the merged `regenInstruction` value is the only channel:
   *   - KS/FU re-run their own ai action, whose ONLY input is `generation_notes`
   *     → pass the instruction there (previously it was silently dropped — the
   *     field was dead for these two types);
   *   - the core docs (jd/inmails/iq/sourcing) take a real `instruction` param
   *     on regenerate_document; `generationNotes` is now '' since the UI no
   *     longer collects a separate notes field.
   */
  async function confirmRegenerate(): Promise<void> {
    const type = regenTarget;
    if (!type) return;
    setRegenTarget(null);
    setActiveType(type);
    try {
      if (type === 'kickoff_sheet') {
        await genKickoff.mutateAsync(regenInstruction || undefined);
      } else if (type === 'client_followup_email') {
        await genFollowup.mutateAsync(regenInstruction || undefined);
      } else {
        await regen.mutateAsync({
          docType: type as RegeneratableDocType,
          instruction: regenInstruction || '',
          generationNotes: '',
        });
      }
      toast.success(`${GENERATION_LABELS[type]} regenerated`);
    } catch {
      toast.error(`Failed to regenerate ${GENERATION_LABELS[type]}`);
    } finally {
      setActiveType(null);
    }
  }

  /**
   * Generate All Docs — KS → batch → FU. The label carries honest progress
   * beyond the 3 static macro-strings (E03-F04-T10 / ISSUE-1): every step is
   * prefixed with a "Step N of M" counter, and while the multi-doc batch step
   * is in flight the label cycles through the doc types that step is expected
   * to produce (there is no per-doc backend stream, so this is a timed cycle,
   * not a real per-doc feed — but it gives the recruiter a live sense of
   * progress rather than a frozen generic string).
   */
  async function generateAll(): Promise<void> {
    // Running total of docs produced across ALL THREE steps (kickoff +1, the
    // batch +res.generated.length, follow-up +1) — NOT just the batch step. The
    // old code reported only the batch's `producedCount`, so the final toast
    // undercounted by the two special items that ran in the same call (ISSUE-2).
    let totalProduced = 0;
    let batchReported = false;
    let cycleTimer: ReturnType<typeof setInterval> | null = null;
    const totalSteps = GENERATE_ALL_STEPS.length;
    // Steps that threw. Each step runs under its OWN try/catch so one failure
    // never aborts the rest (E03-F04-T04 acceptance #2, legacy
    // project-docs.js generateAllDocuments: per-step try/catch + console.warn).
    const failedSteps: string[] = [];
    /** Run ONE Generate-All step. Throws on failure — the loop catches per step. */
    async function runStep(action: GenerateAllAction, prefix: string): Promise<void> {
      if (action === 'generate_kickoff_sheet') {
        await genKickoff.mutateAsync(notes || undefined);
        totalProduced += 1;
      } else if (action === 'generate_documents') {
        // Cycle the label through the batch's expected doc types on a timer so
        // the user sees which docs this step covers + a "K of N" counter. The
        // denominator comes from GENERATE_DOCUMENTS_TYPES (4) — never a stale 3.
        let idx = 0;
        const tick = (): void => {
          const dt = GENERATE_ALL_BATCH_DOCS[idx % GENERATE_ALL_BATCH_DOCS.length]!;
          setGenerateAllStep(
            `${prefix} · Generating ${BATCH_DOC_LABELS[dt]} ` +
              `(${(idx % GENERATE_ALL_BATCH_DOCS.length) + 1} of ${GENERATE_ALL_BATCH_DOCS.length})…`,
          );
          idx += 1;
        };
        tick();
        cycleTimer = setInterval(tick, 1200);
        try {
          // The batch throws (backend 502 GENERATION_EMPTY) when nothing was
          // produced — so reaching here means at least the call succeeded. Still
          // guard an explicitly-empty array as a failure (honesty over a fake ok).
          const res = await genDocs.mutateAsync(notes || undefined);
          if (Array.isArray(res.generated) && res.generated.length === 0) {
            throw new Error('generate_documents produced no documents');
          }
          if (Array.isArray(res.generated)) {
            totalProduced += res.generated.length;
            batchReported = true;
          }
        } finally {
          clearInterval(cycleTimer);
          cycleTimer = null;
        }
      } else if (action === 'generate_followup_email') {
        await genFollowup.mutateAsync(notes || undefined);
        totalProduced += 1;
      }
    }
    try {
      for (let i = 0; i < GENERATE_ALL_STEPS.length; i += 1) {
        const step = GENERATE_ALL_STEPS[i]!;
        const prefix = `Step ${i + 1} of ${totalSteps}`;
        setGenerateAllStep(`${prefix} · ${step.label}`);
        try {
          await runStep(step.action, prefix);
        } catch (err) {
          // Record + continue with the next step — never abort Generate-All.
          console.warn(`Generate All: step "${step.action}" failed`, err);
          failedSteps.push(GENERATE_ALL_STEP_NAMES[step.action]);
        }
      }
      const docsWord = `document${totalProduced === 1 ? '' : 's'}`;
      if (failedSteps.length === 0) {
        // Report the TRUE total across kickoff + batch + follow-up. Only fall back
        // to the flat message when the batch never reported a count (so the total
        // would be misleadingly low).
        toast.success(
          batchReported ? `Generated ${totalProduced} ${docsWord}` : 'All documents generated',
        );
      } else if (failedSteps.length === totalSteps) {
        toast.error('Generate All failed');
      } else {
        // Partial: name the failed step(s) so the recruiter can retry just those
        // cards; the successful steps' docs are already saved.
        toast.error(
          `Generate All: failed — ${failedSteps.join(', ')}. ` +
            `Other steps completed (${totalProduced} ${docsWord} generated).`,
        );
      }
    } finally {
      if (cycleTimer) clearInterval(cycleTimer);
      setGenerateAllStep(null);
    }
  }

  const generatingAll = generateAllStep !== null;

  return (
    <div className="flex flex-col lg:flex-row gap-6" data-testid="generation-stage">
      <div className="flex-1 flex flex-col gap-4">
        {/* Header: counters + Generate All + AI instructions */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-sm text-[var(--color-text-muted)]">
            <span data-testid="gen-count-generated">
              Generated: <strong className="text-[var(--color-text-strong)]">{generated}</strong>
            </span>
            <span data-testid="gen-count-approved">
              Approved: <strong className="text-[var(--color-text-strong)]">{approved}</strong>
            </span>
          </div>
          <button
            type="button"
            data-testid="gen-generate-all"
            disabled={activeType !== null || generatingAll}
            onClick={() => void generateAll()}
            className="min-h-[40px] px-4 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)] disabled:opacity-60 inline-flex items-center gap-2"
          >
            {generatingAll && (
              <span
                data-testid="gen-all-spinner"
                className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                aria-hidden="true"
              />
            )}
            <span data-testid="gen-all-label">
              {generatingAll ? generateAllStep : 'Generate All Docs'}
            </span>
          </button>
        </div>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-[var(--color-text-strong)]">AI Instructions</span>
          <textarea
            data-testid="gen-instructions"
            className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[60px] outline-none focus:border-[var(--color-accent)]"
            placeholder="Optional instructions applied to every generation…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => onSaveGenerationNotes?.(notes)}
          />
        </label>

        {/* Generation cards in GENERATION_ORDER — 2-column grid (legacy v1 look) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {GENERATION_CARDS.map((type) => {
            const status = artifactStatusOf(artifacts, type);
            const actions = cardActionsFor(type, status);
            // Only THIS card locks while its own generate/regenerate is in flight.
            const cardBusy = activeType === type;
            return (
              <div
                key={type}
                data-testid={`gen-card-${type}`}
                className={`rounded-lg border p-4 flex flex-col gap-3 bg-white ${
                  status === 'approved' || status === 'sent'
                    ? 'border-[var(--color-accent)]'
                    : 'border-[var(--color-border)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span aria-hidden="true" className="text-xl leading-none mt-0.5">
                      {GENERATION_ICONS[type]}
                    </span>
                    <div className="flex flex-col">
                      <span className="font-medium text-[var(--color-text-strong)]">
                        {GENERATION_LABELS[type]}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {GENERATION_DESCRIPTIONS[type]}
                      </span>
                    </div>
                  </div>
                  <StatusBadge type={type} map={artifacts} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {actions.canGenerate && (
                    <button
                      type="button"
                      data-testid={`gen-generate-${type}`}
                      disabled={cardBusy || generatingAll}
                      onClick={() => void generateCard(type)}
                      className="min-h-[36px] px-3 rounded-md bg-[var(--color-accent)] text-[var(--color-primary)] text-sm font-medium hover:bg-[var(--color-accent-hover)] disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      {cardBusy && (
                        <span
                          data-testid={`gen-spinner-${type}`}
                          className="inline-block w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin"
                          aria-hidden="true"
                        />
                      )}
                      {cardBusy ? 'Generating…' : 'Generate'}
                    </button>
                  )}
                  {actions.canView && (
                    <button
                      type="button"
                      data-testid={`gen-view-${type}`}
                      onClick={() => onOpenEditor?.(type)}
                      className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)]"
                    >
                      View / Edit
                    </button>
                  )}
                  {actions.canRegenerate && (
                    <button
                      type="button"
                      data-testid={`gen-regenerate-${type}`}
                      disabled={cardBusy || generatingAll}
                      onClick={() => void regenerateCard(type)}
                      className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)] disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      {cardBusy && (
                        <span
                          data-testid={`gen-spinner-${type}`}
                          className="inline-block w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin"
                          aria-hidden="true"
                        />
                      )}
                      {cardBusy ? 'Generating…' : 'Regenerate'}
                    </button>
                  )}
                  {actions.canApprove && (
                    <button
                      type="button"
                      data-testid={`gen-approve-${type}`}
                      onClick={() => void approve.mutateAsync(type)}
                      className="min-h-[36px] px-3 rounded-md border border-[var(--color-success)] text-[var(--color-success)] text-sm hover:bg-[var(--color-success)]/10"
                    >
                      Approve
                    </button>
                  )}
                  {actions.canDownload && (
                    <button
                      type="button"
                      data-testid={`gen-download-${type}`}
                      onClick={() => onDownload?.(type)}
                      className="min-h-[36px] px-3 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)]"
                    >
                      Download
                    </button>
                  )}
                  {actions.canSend && (
                    <button
                      type="button"
                      data-testid={`gen-send-${type}`}
                      onClick={() => onSend?.(type)}
                      className="min-h-[36px] px-3 rounded-md bg-[var(--color-primary)] text-white text-sm hover:bg-[var(--color-primary-hover)]"
                    >
                      Send
                    </button>
                  )}
                  {type === 'kickoff_sheet' && status !== 'not_generated' && (
                    <button
                      type="button"
                      data-testid="gen-send-to-sourcing"
                      onClick={() => onSendToSourcing?.()}
                      className="min-h-[36px] px-3 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:bg-[var(--color-primary-hover)]"
                    >
                      Send to Sourcing Team
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* Internal Notification block (full-width card spanning both columns) */}
          <div
            data-testid="gen-card-internal_notification"
            className="md:col-span-2 rounded-lg border border-[var(--color-border)] p-4 flex items-center justify-between gap-3 bg-[var(--color-surface-muted)]"
          >
            <div className="flex items-center gap-3">
              <span aria-hidden="true" className="text-xl leading-none">
                {GENERATION_ICONS.internal_notification}
              </span>
              <div className="flex flex-col">
                <span className="font-medium text-[var(--color-text-strong)]">
                  {GENERATION_LABELS.internal_notification}
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                  {GENERATION_DESCRIPTIONS.internal_notification}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* E03-F07-T07 — persisted Sent state (latest internal_notification_sent
                  event), so a send is visible and survives a reload. */}
              {notifSentLabel && (
                <span
                  data-testid="gen-badge-internal_notification"
                  data-status="sent"
                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${badgeFor('sent').toneClass}`}
                >
                  {notifSentLabel}
                </span>
              )}
              <button
                type="button"
                data-testid="gen-notify"
                onClick={() => onSendNotification?.()}
                className="min-h-[36px] px-3 rounded-md bg-[var(--color-primary)] text-white text-sm hover:bg-[var(--color-primary-hover)]"
              >
                {notifSentLabel ? 'Send again' : 'Send'}
              </button>
            </div>
          </div>

          {/* Confirm & Move to Final — enabled only when every core doc is approved
              (legacy confirmDocumentPackage → confirm_package + advance to stage 4). */}
          {onConfirmPackage && (
            <div className="md:col-span-2 mt-2 flex justify-end border-t border-[var(--color-border)] pt-4">
              <button
                type="button"
                data-testid="gen-confirm-package"
                disabled={!allApproved || confirming}
                onClick={() => onConfirmPackage()}
                title={
                  allApproved
                    ? 'Confirm the document package and move the project to Final'
                    : `All documents must be approved before confirming (${gate.approvedCount} of ${gate.requiredCount}) — still needed: ${gate.missing
                        .map((t) => GENERATION_LABELS[t as GenerationOrderItem] ?? t)
                        .join(', ')}`
                }
                className="min-h-[40px] px-4 rounded-md bg-[var(--color-success)] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {confirming && (
                  <span
                    data-testid="gen-confirm-spinner"
                    className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                    aria-hidden="true"
                  />
                )}
                Confirm &amp; Move to Final
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Kickoff Data side panel */}
      {kickoffData && (
        <aside
          data-testid="gen-kickoff-panel"
          className="lg:w-72 shrink-0 rounded-lg border border-[var(--color-border)] p-4 bg-white"
        >
          <h3 className="text-sm font-semibold text-[var(--color-text-strong)] mb-2">
            Kickoff Data
          </h3>
          {kickoffData}
        </aside>
      )}

      {/* Card-level Regenerate dialog — Instruction entered BEFORE the API call
          fires (E03-F05-T09 / ISSUE-4). Same shell + testids as the
          DocumentEditor regenerate dialog. */}
      <Dialog
        open={regenTarget !== null}
        onClose={() => setRegenTarget(null)}
        title={`Regenerate ${regenTarget ? GENERATION_LABELS[regenTarget] : 'document'}`}
        testId="doc-regen-dialog"
        footer={
          <DialogFooter
            onCancel={() => setRegenTarget(null)}
            onConfirm={() => void confirmRegenerate()}
            confirmLabel="Regenerate"
            variant="accent"
            testId="doc-regen-footer"
          />
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--color-text-strong)]">
              Regeneration instructions
            </span>
            <textarea
              data-testid="doc-regen-instruction"
              className="border border-[var(--color-border)] rounded-md p-2 text-sm min-h-[240px] outline-none focus:border-[var(--color-accent)]"
              placeholder="e.g. make the tone more formal, regenerate the whole document in Chinese…"
              value={regenInstruction}
              onChange={(e) => setRegenInstruction(e.target.value)}
            />
          </label>
        </div>
      </Dialog>
    </div>
  );
}
